/**
 * M3X.WORKSPACE F.7 — AI brief panel.
 *
 * v1 shipped static-slot from M3X.WORKSPACE F.7. Session 3a PR 1
 * activates the live `/api/ai-brief` round-trip — Sonnet narration
 * when a per-tenant Anthropic key is in the M8 vault, deterministic
 * template otherwise.
 *
 * Visual contract per spec §0.6:
 *   - 280px right column
 *   - Agent badge in header (Tashghil / Mutaba'aa / Rawatib / Khidmat)
 *   - Brief content area
 *   - Empty state: "✦ No anomalies detected on this page" in brand teal
 *
 * Callers may STILL pass `children` to override the live brief
 * (e.g. when the page builds its own bespoke insight cards).
 * When `children` is omitted AND `liveContext` is provided, the
 * panel hits `/api/ai-brief` with the current route + context
 * and renders the response.
 */

import { useQuery } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import { useLocation } from "react-router-dom";

import { api } from "../lib/api";


export type BriefAgent = "tashghil" | "mutabaa" | "rawatib" | "khidmat";


const _AGENT_LABELS_EN: Record<BriefAgent, string> = {
  tashghil: "Tashghil",
  mutabaa: "Mutaba'aa",
  rawatib: "Rawatib",
  khidmat: "Khidmat",
};


const _AGENT_LABELS_AR: Record<BriefAgent, string> = {
  tashghil: "تشغيل",
  mutabaa: "متابعة",
  rawatib: "رواتب",
  khidmat: "خدمات",
};


export interface AiBriefLiveContext {
  /** Total rows on the screen. */
  total?: number;
  /** Rows flagged for review. */
  attention?: number;
  /** Critical-severity rows. */
  critical?: number;
  /** Most common category in the visible data. */
  top_kind?: string;
  /** Window label e.g. "this week". */
  window_label?: string;
}


export interface AiBriefPanelProps {
  agent: BriefAgent;
  /**
   * Static brief content. When provided, overrides the live fetch
   * entirely. When omitted AND `liveContext` is provided, the panel
   * fetches from `/api/ai-brief` with the current route + context.
   */
  children?: React.ReactNode;
  /** Optional title override. Defaults to "AI Brief". */
  title?: string;
  locale?: "en" | "ar";
  /** Context payload threaded into `/api/ai-brief`. */
  liveContext?: AiBriefLiveContext;
}


function _LiveBrief({
  route,
  locale,
  context,
}: {
  route: string;
  locale: "en" | "ar";
  context: AiBriefLiveContext;
}) {
  const q = useQuery({
    queryKey: ["ai-brief", route, locale, context],
    queryFn: () =>
      api.getAiBrief(
        route,
        locale,
        context as unknown as Record<string, unknown>,
      ),
    staleTime: 30_000,
  });

  if (q.isLoading) {
    return (
      <div className="space-y-2 px-2 py-3">
        <div className="h-3 w-3/4 animate-pulse rounded bg-surface-sunken" />
        <div className="h-2 w-full animate-pulse rounded bg-surface-sunken" />
        <div className="h-2 w-5/6 animate-pulse rounded bg-surface-sunken" />
      </div>
    );
  }

  if (q.isError || !q.data) {
    return (
      <p className="px-2 py-4 text-center text-sm text-ai-accent">
        ✦{" "}
        {locale === "ar"
          ? "لم يتم اكتشاف أي شذوذ في هذه الصفحة"
          : "No anomalies detected on this page"}
      </p>
    );
  }

  const brief = q.data;
  return (
    <div className="space-y-2 px-2 py-3 text-sm">
      <p className="font-medium text-text-primary">
        {brief.headline}
      </p>
      <ul className="space-y-1 text-text-secondary">
        {brief.bullets.map((b, i) => (
          <li key={i} className="text-xs">
            • {b.text}
          </li>
        ))}
      </ul>
      {brief.action && (
        <button
          type="button"
          className="mt-1 inline-flex items-center rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-2 py-1 text-[11px] font-medium text-white hover:opacity-95"
          onClick={() => {
            if (brief.action?.href) window.location.href = brief.action.href;
          }}
        >
          {brief.action.label}
        </button>
      )}
      {brief.source === "deterministic" && (
        <p className="mt-1 text-[10px] text-text-tertiary">
          {locale === "ar"
            ? "السرد الذكي يفتح عند توفير مفتاح الموزع."
            : "Live narration activates with the tenant Anthropic key."}
        </p>
      )}
    </div>
  );
}


export function AiBriefPanel({
  agent,
  children,
  title,
  locale = "en",
  liveContext,
}: AiBriefPanelProps) {
  const location = useLocation();
  const agentLabel =
    locale === "ar" ? _AGENT_LABELS_AR[agent] : _AGENT_LABELS_EN[agent];
  const useLive = !children && liveContext !== undefined;

  return (
    <div className="flex h-full flex-col p-3">
      <div className="mb-3 flex items-center gap-2 border-b border-border-subtle pb-2">
        <Sparkles
          className="h-4 w-4 text-ai-accent"
          aria-hidden="true"
        />
        <span className="text-sm font-semibold text-text-primary">
          {title ??
            (locale === "ar" ? "موجز الذكاء الاصطناعي" : "AI Brief")}
        </span>
        <span
          className="ms-auto rounded-full border border-border-subtle px-2 py-0.5 text-xs text-text-tertiary"
          data-agent={agent}
        >
          ✦ {agentLabel}
        </span>
      </div>

      <div className="flex-1 overflow-y-auto">
        {children ? (
          children
        ) : useLive ? (
          <_LiveBrief
            route={location.pathname}
            locale={locale}
            context={liveContext}
          />
        ) : (
          <p className="px-2 py-4 text-center text-sm text-ai-accent">
            ✦{" "}
            {locale === "ar"
              ? "لم يتم اكتشاف أي شذوذ في هذه الصفحة"
              : "No anomalies detected on this page"}
          </p>
        )}
      </div>
    </div>
  );
}


export default AiBriefPanel;
